import { create } from 'zustand'

export type Tool = 
  | 'select' 
  | 'move' 
  | 'rotate' 
  | 'scale' 
  | 'sketch' 
  | 'extrude' 
  | 'revolve'
  | 'fillet'
  | 'chamfer'
  | 'shell'
  | 'pattern'
  | 'mirror'
  | 'measure'
  | 'section'

export interface Feature {
  id: string
  type: 'sketch' | 'extrude' | 'revolve' | 'fillet' | 'chamfer' | 'shell' | 'pattern' | 'mirror' | 'import'
  name: string
  visible: boolean
  locked: boolean
  parameters: Record<string, unknown>
  children?: Feature[]
}

export interface Document {
  id: string
  name: string
  modified: Date
  features: Feature[]
  thumbnail?: string
}

interface SelectionState {
  selectedIds: string[]
  hoveredId: string | null
}

interface ViewportState {
  camera: {
    position: [number, number, number]
    target: [number, number, number]
  }
  gridVisible: boolean
  axesVisible: boolean
  projection: 'perspective' | 'orthographic'
}

interface CADState {
  currentDocument: Document | null
  activeTool: Tool
  selection: SelectionState
  viewport: ViewportState
  isModified: boolean
  undoStack: unknown[]
  redoStack: unknown[]
  
  setDocument: (doc: Document | null) => void
  setActiveTool: (tool: Tool) => void
  setSelection: (ids: string[]) => void
  addToSelection: (id: string) => void
  removeFromSelection: (id: string) => void
  clearSelection: () => void
  setHovered: (id: string | null) => void
  toggleGrid: () => void
  toggleAxes: () => void
  setProjection: (projection: 'perspective' | 'orthographic') => void
  setCameraPosition: (position: [number, number, number]) => void
  addFeature: (feature: Feature) => void
  updateFeature: (id: string, updates: Partial<Feature>) => void
  deleteFeature: (id: string) => void
  toggleFeatureVisibility: (id: string) => void
  undo: () => void
  redo: () => void
}

const defaultDocument: Document = {
  id: 'doc-1',
  name: 'Untitled Design',
  modified: new Date(),
  features: [
    {
      id: 'feat-1',
      type: 'sketch',
      name: 'Base Sketch',
      visible: true,
      locked: false,
      parameters: { plane: 'XY' },
    },
    {
      id: 'feat-2',
      type: 'extrude',
      name: 'Base Extrusion',
      visible: true,
      locked: false,
      parameters: { height: 20, direction: 'positive' },
    },
    {
      id: 'feat-3',
      type: 'fillet',
      name: 'Edge Fillets',
      visible: true,
      locked: false,
      parameters: { radius: 2 },
    },
  ],
}

export const useCADStore = create<CADState>((set, get) => ({
  currentDocument: defaultDocument,
  activeTool: 'select',
  selection: {
    selectedIds: [],
    hoveredId: null,
  },
  viewport: {
    camera: {
      position: [50, 50, 50],
      target: [0, 0, 0],
    },
    gridVisible: true,
    axesVisible: true,
    projection: 'perspective',
  },
  isModified: false,
  undoStack: [],
  redoStack: [],

  setDocument: (doc) => set({ currentDocument: doc, isModified: false }),
  
  setActiveTool: (tool) => set({ activeTool: tool }),
  
  setSelection: (ids) =>
    set((state) => ({
      selection: { ...state.selection, selectedIds: ids },
    })),
  
  addToSelection: (id) =>
    set((state) => ({
      selection: {
        ...state.selection,
        selectedIds: [...state.selection.selectedIds, id],
      },
    })),
  
  removeFromSelection: (id) =>
    set((state) => ({
      selection: {
        ...state.selection,
        selectedIds: state.selection.selectedIds.filter((i) => i !== id),
      },
    })),
  
  clearSelection: () =>
    set((state) => ({
      selection: { ...state.selection, selectedIds: [] },
    })),
  
  setHovered: (id) =>
    set((state) => ({
      selection: { ...state.selection, hoveredId: id },
    })),
  
  toggleGrid: () =>
    set((state) => ({
      viewport: { ...state.viewport, gridVisible: !state.viewport.gridVisible },
    })),
  
  toggleAxes: () =>
    set((state) => ({
      viewport: { ...state.viewport, axesVisible: !state.viewport.axesVisible },
    })),
  
  setProjection: (projection) =>
    set((state) => ({
      viewport: { ...state.viewport, projection },
    })),
  
  setCameraPosition: (position) =>
    set((state) => ({
      viewport: {
        ...state.viewport,
        camera: { ...state.viewport.camera, position },
      },
    })),
  
  addFeature: (feature) =>
    set((state) => {
      if (!state.currentDocument) return state
      return {
        currentDocument: {
          ...state.currentDocument,
          features: [...state.currentDocument.features, feature],
          modified: new Date(),
        },
        isModified: true,
      }
    }),
  
  updateFeature: (id, updates) =>
    set((state) => {
      if (!state.currentDocument) return state
      return {
        currentDocument: {
          ...state.currentDocument,
          features: state.currentDocument.features.map((f) =>
            f.id === id ? { ...f, ...updates } : f
          ),
          modified: new Date(),
        },
        isModified: true,
      }
    }),
  
  deleteFeature: (id) =>
    set((state) => {
      if (!state.currentDocument) return state
      return {
        currentDocument: {
          ...state.currentDocument,
          features: state.currentDocument.features.filter((f) => f.id !== id),
          modified: new Date(),
        },
        isModified: true,
      }
    }),
  
  toggleFeatureVisibility: (id) =>
    set((state) => {
      if (!state.currentDocument) return state
      return {
        currentDocument: {
          ...state.currentDocument,
          features: state.currentDocument.features.map((f) =>
            f.id === id ? { ...f, visible: !f.visible } : f
          ),
        },
      }
    }),
  
  undo: () => {
    const { undoStack } = get()
    if (undoStack.length === 0) return
  },
  
  redo: () => {
    const { redoStack } = get()
    if (redoStack.length === 0) return
  },
}))
